// Libraries
import I18n from 'i18next'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import React, { Component, Fragment } from 'react'
import { bindActionCreators } from 'redux'
import { message, Modal } from 'antd'
import { get, includes, defaultTo, isNil, map } from 'lodash'

// Ressources
import { actions as preventionPlanActions } from 'Resources/PreventionPlansResource'
import {
  actions as companyActions,
  fetchCompaniesPdpNums
} from 'Resources/CompanyResource'

// Common components
import { ManageModal } from 'Components'

// Specs
import { ModalSpecs } from 'Views/PreventionPlans/Specs'

// Écrans
import { List } from './Screens'
import { formatOrderedData } from 'Helpers/index'
import moment from 'moment'

const { confirm } = Modal

class PreventionPlansConstructor extends Component {
  static propTypes = {
    // Actions d'API
    actions: PropTypes.object,

    // Données de la vue
    preventionPlan: PropTypes.object,
    preventionPlans: PropTypes.array,
    avenants: PropTypes.array,

    // Relationnal data for fields
    companies: PropTypes.array,

    // Témoin d'activité API
    isGathering: PropTypes.bool,
    isUpdating: PropTypes.bool,
    isCreating: PropTypes.bool,
    isFetchingCompanies: PropTypes.bool,

    // Utilisateur connecté
    profile: PropTypes.object,

    // Navigation interne
    navigation: PropTypes.shape({
      navigate: PropTypes.func.isRequired,
      getParam: PropTypes.func.isRequired,
      setParams: PropTypes.func.isRequired
    })
  }

  static defaultProps = {
    // Actions d'API
    actions: {},

    // Données de la vue
    preventionPlan: {},
    preventionPlans: [],
    avenants: [],

    // Relationnal data for fields
    companies: [],

    // Utilisateur connecté
    profile: {},

    // Témoin d'activité API
    isGathering: false,
    isUpdating: false,
    isCreating: false,
    isFetchingCompanies: false
  }

  constructor(props) {
    super(props)

    this.state = {
      openedModal: null,
      allCompaniesPdpnums: [],
      isFetchingCompanyPdpnums: false
    }
  }

  componentDidMount = () => {
    // Récupération des données des éléments de la vue
    this.fetchCompaniesData()
    this.fetchPreventionPlansData()
    this.fetchAvenants()

    if (this.getContext() === 'single') {
      this.getPreventionPlanData().then(this.askUpdatePreventionPlan)
    }
  }

  /**
   * Récupération du contexte actuel du constructeur de page
   */
  getContext = () => {
    return isNil(this.props.navigation.getParam('id')) ? 'list' : 'single'
  }

  /**
   * Récupération des données des éléments de la vue
   */
  fetchPreventionPlansData = () => {
    this.props.actions.fetchPreventionPlans().catch(() => {
      message.error(I18n.t('api.errors.preventionPlan.fetch'))
    })
  }

  /**
   * Récupération des données des compagnies pour les champs relationnels
   */
  fetchAvenants = () => {
    this.props.actions.fetchAvenantsPreventionPlan().catch(e => {
      e.status === 403
        ? message.error(I18n.t('api.errors.exceptions.403.desc'))
        : message.error(I18n.t('api.errors.user.fetch'))
    })
  }

  /**
   * Récupération des données des compagnies pour les champs relationnels
   */
  fetchCompaniesData = () => {
    this.props.actions.fetchAllCompanies().catch(e => {
      e.status === 403
        ? message.error(I18n.t('api.errors.exceptions.403.desc'))
        : message.error(I18n.t('api.errors.user.fetch'))
    })
  }

  rebuildCompaniesPdpNums = async ids => {
    this.setState({ isFetchingCompanyPdpnums: true })
    const allCompaniesPdpnums = await fetchCompaniesPdpNums(
      this.props.actions.fetchCompanyPdpnums,
      ids
    )
    this.setState({ allCompaniesPdpnums, isFetchingCompanyPdpnums: false })
  }
  /**
   * Récupération des données détaillées d'un élément de la vue
   */
  getPreventionPlanData = (
    preventionPlanId = this.props.navigation.getParam('id')
  ) =>
    new Promise((resolve, reject) => {
      this.props.actions
        .getPreventionPlan(preventionPlanId)
        .then(response => {
          const preventionPlan = get(response, 'body')

          // Chargement des pdpnums liés aux companies sélectionnées
          const companiesIds = get(preventionPlan, 'companies')?.map(
            company => company.id
          )

          this.onCompaniesChange(null, companiesIds)

          // Détails récupérés
          resolve(preventionPlan, response)
        })
        .catch(error => {
          // Erreur lors du refresh
          reject(error)
          message.error(I18n.t('api.errors.preventionPlan.get'))
        })
    })


  // /**
  //  * Actions groupées:
  //  * Suppression de plusieurs lignes du tableau
  //  */
  deleteManyPreventionPlan = (preventionPlan, clearTableSelections) => {
    console.log(this.props.actions)

    this.props.actions
      .deletePreventionPlans(preventionPlan)
      .then(() => {
        clearTableSelections()
        message.success(I18n.t('api.success.preventionPlan.bulk.delete'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.preventionPlan.bulk.delete'))
      })
  }

  /**
   * Suppression d'une ligne du tableau
   */
  deletePreventionPlan = preventionPlan => {
    this.props.actions
      .deletePreventionPlan(preventionPlan)
      .then(() => {
        this.safelyCloseModal()
        this.props.navigation.setParams({ id: null })
        message.success(I18n.t('api.success.preventionPlan.delete'))
      })
      .catch(() => {
        message.error(I18n.t('api.errors.preventionPlan.delete'))
      })
  }

  /**
   * Création d'un preventionPlan
   */
  createPreventionPlan = createPreventionPlan =>
    new Promise((resolve, reject) => {
      // Nettoyage des données (évite backend validation error)
      let isAvenantOf = get(createPreventionPlan, 'isAvenantOf')
      if (Array.isArray(isAvenantOf) && isAvenantOf.length === 0) {
        isAvenantOf = undefined
      }

      let typePdp = get(createPreventionPlan, 'typePdp')
      let isAvenant = typePdp === 'AVENANT'
      console.log("isAvenant")
      console.log(isAvenant)
      if (!isAvenant) {
        isAvenantOf = null
      }

      const payload = {
        ...createPreventionPlan,
        isAvenant: isAvenant,
        startDate: moment(createPreventionPlan.startDate).format('YYYY-MM-DD'),
        endDate: moment(createPreventionPlan.endDate).format('YYYY-MM-DD'),
        isAvenantOf
      }
      this.props.actions
        .createPreventionPlan(payload)
        .then(response => {
          this.safelyCloseModal()
          this.fetchAvenants()
          resolve(get(response, 'body'), response)
          message.success(I18n.t('api.success.preventionPlan.create'))
        })
        .catch(error => {
          reject(error)
          message.error(I18n.t('api.errors.preventionPlan.create'))
        })
    })

  updatePreventionPlan = (updatedPlan, originalPlan) =>
    new Promise((resolve, reject) => {
      // Nettoyage des données (évite backend validation error)
      let isAvenantOf = get(updatedPlan, 'isAvenantOf')
      if (Array.isArray(isAvenantOf) && isAvenantOf.length === 0) {
        isAvenantOf = undefined
      }

      let isAvenant = get(updatedPlan, 'isAvenant')
      if (!isAvenant) {
        isAvenantOf = null
      }

      const payload = {
        id: get(originalPlan, 'id'),
        ...updatedPlan,
        startDate: moment(updatedPlan.startDate).format('YYYY-MM-DD'),
        endDate: moment(updatedPlan.endDate).format('YYYY-MM-DD'),
        isAvenantOf
      }

      this.props.actions
        .updatePreventionPlan(payload)
        .then(response => {
          this.safelyCloseModal()
          resolve(get(response, 'body'), response)
          message.success(I18n.t('api.success.preventionPlan.update'))
        })
        .catch(error => {
          reject(error)
          message.error(I18n.t('api.errors.preventionPlan.update'))
        })
    })



  /**
   * Actions groupées:
   * Demande d'archivage de plusieurs lignes du tableau
   */
  askDeleteManyPreventionPlans = (...params) => {
    confirm({
      title: I18n.t('pages.preventionPlans.modal.delete.multiple.title'),
      content: I18n.t('pages.preventionPlans.modal.delete.multiple.content'),
      okText: I18n.t('pages.preventionPlans.modal.delete.multiple.okText'),
      okType: 'danger',
      cancelText: I18n.t(
        'pages.preventionPlans.modal.delete.multiple.cancelText'
      ),
      onOk: () => {
        this.deleteManyPreventionPlan(...params)
      },
      maskClosable: true
    })
  }

  /**
   * Demande d'archivage d'une ligne du tableau
   */
  askDeletePreventionPlan = () => {
    const { preventionPlan } = this.props

    confirm({
      title: I18n.t('pages.preventionPlans.modal.delete.single.title'),
      content: I18n.t('pages.preventionPlans.modal.delete.single.content'),
      okText: I18n.t('pages.preventionPlans.modal.delete.single.okText'),
      okType: 'danger',
      cancelText: I18n.t(
        'pages.preventionPlans.modal.delete.single.cancelText'
      ),
      onOk: () => {
        this.deletePreventionPlan(preventionPlan)
      },
      maskClosable: true
    })
  }

  /**
   * Demande de modification d'une ligne du tableau
   */
  askUpdatePreventionPlan = () => {
    this.setState({ openedModal: 'updatePreventionPlan' })
  }

  /**
   * Demande de création d'une ligne du tableau
   */
  askCreatePreventionPlan = () => {
    this.setState({ openedModal: 'createPreventionPlan' })
  }

  /**
   * Fermeture de toutes les modales
   */
  safelyCloseModal = () => {
    const { openedModal } = this.state

    if (openedModal === 'updatePreventionPlan') {
      this.setState({ openedModal: 'transition' }, () => {
        setTimeout(
          this.setState({ openedModal: null }, () => {
            this.props.navigation.setParams({ id: null })
          }),
          200
        )
      })
    } else {
      this.setState({ openedModal: null })
    }
  }

  /**
   * Callback:
   * Création d'une ligne de tableau réussie
   */
  handleRowCreated = () => {
    this.safelyCloseModal()
  }

  /**
   * Callback:
   * Modification d'une ligne de tableau réussie
   */
  handleRowUpdated = () => {
    this.safelyCloseModal()
  }

  /**
   * Clic sur une ligne du tableau
   */
  handleRowClick = preventionPlan => {
    // Navigation vers la page détaillée
    this.props.navigation.setParams({ id: get(preventionPlan, 'id') })

    this.getPreventionPlanData(preventionPlan)
      .then(() => {
        // Ouverture de la modale
        this.askUpdatePreventionPlan()
      })
      .catch(() => {
        this.props.navigation.setParams({ id: null })
      })

    // Ouverture du drawer
    // this.setState({ drawerOpened: true })

    // Navigation vers la vue détaillée
    // this.props.navigation.navigate('PreventionPlan', { id: get(preventionPlan, 'id') })
  }

  handleDraggedRowReleased = orderedData => {
    const formattedOrderedData = formatOrderedData(orderedData)

    this.props.actions
      .orderPreventionPlan(formattedOrderedData)
      .then(() => message.success(I18n.t('api.success.preventionPlan.order')))
      .catch(() => message.error(I18n.t('api.errors.preventionPlan.order')))
  }

  /**
   * Formatage des lignes de la modale de modification / création
   */
  formatModalRows = rows => {
    return rows
  }

  /**
   * Companies / pdpnum related logic
   * Pbpnums are related to companies
   * On "companies" change, we clear "pdpnum" field and refetch available pdpnums
   */

  onValuesChange = async (form, changedValues, allValues) => {
    // Wait for the form to be updated before to be able to manipulate values (like resetting a field)
    await Promise.resolve().then(() => {
      if (changedValues.companies) {
        this.onCompaniesChange(form, changedValues.companies)
      }
    })
  }

  onCompaniesChange = async (form, companies) => {
    this.resetPdpNumField(form)
    this.rebuildCompaniesPdpNums(companies)
  }

  resetPdpNumField = (form = null) => {
    if (form) {
      form.setFieldsValue({ pdpnum: undefined })
    }
  }

  render() {
    // Props
    const {
      isGathering,
      isUpdating,
      isCreating,
      isFetchingCompanies,
      preventionPlan,
      preventionPlans,
      avenants,
      companies,
      profile
    } = this.props

    const isAdmin = map(get(profile, 'roles'), 'name').some(role =>
      includes(['ADMIN', 'SUPERADMIN'], role)
    )

    // State
    const {
      openedModal,
      allCompaniesPdpnums,
      isFetchingCompanyPdpnums
    } = this.state

    // Chargement
    const loading = isGathering || isUpdating || isCreating
    const modalLoading =
      loading || isFetchingCompanies || isFetchingCompanyPdpnums

    // Création de la liste des fonctions constructeur exposés au écrans
    const constructorFunctions = {
      // Clic sur un ligne de tableau
      handleRowClick: this.handleRowClick,

      // Gestion de la modale
      safelyCloseModal: this.safelyCloseModal,

      // Récupération des données détaillées d'une ligne du tableau
      getPreventionPlanData: this.getPreventionPlanData,

      // Création d'une ligne de tableau
      handleRowCreated: this.handleRowCreated,
      askCreatePreventionPlan: this.askCreatePreventionPlan,

      // Modification d'une ou plusieurs ligne(s) de tableau
      handleRowUpdated: this.handleRowUpdated,
      askUpdatePreventionPlan: this.askUpdatePreventionPlan,
      askUpdateManyPreventionPlans: this.askUpdateManyPreventionPlans,

      handleDraggedRowReleased: this.handleDraggedRowReleased,

      // Archivage d'une ou plusieurs ligne(s) du tableau
      askArchivePreventionPlan: this.askArchivePreventionPlan,
      askArchiveManyPreventionPlans: this.askArchiveManyPreventionPlans,

      // Suppression d'une ou plusieurs ligne(s) du tableau
      askDeletePreventionPlan: this.askDeletePreventionPlan,
      askDeleteManyPreventionPlans: this.askDeleteManyPreventionPlans
    }

    // Ajout des actions sur la modale
    ModalSpecs.actions.delete.onClick = this.askDeletePreventionPlan
    ModalSpecs.actions.delete.hidden = !isAdmin

    // Fields populated with data
    ModalSpecs.rows.pdpnum.formField.options = map(
      allCompaniesPdpnums,
      ({ name, pdpnum, id }) => ({
        id,
        value: id,
        label: `${name} (${pdpnum})`
      })
    )

    // Fields populated with data
    ModalSpecs.rows.companies.formField.options = map(
      companies || [],
      ({ name, id }) => ({
        value: id,
        id,
        label: name
      })
    )
    ModalSpecs.rows.subcontractors.formField.options = map(
      companies || [],
      ({ name, id }) => ({
        value: id,
        id,
        label: name
      })
    )
    ModalSpecs.rows.isAvenantOf.formField.options = map(
      avenants || [],
      ({ pdpnum, id }) => ({
        value: id,
        id,
        label: pdpnum
      })
    )

    // Ajout des lignes au formulaire
    // formLayout = merge(formLayout, manageModal)

    // console.log('preventionPlans', preventionPlans)
    // console.log('preventionPlan (single)', preventionPlan)

    return (
      <Fragment>
        {/* Rendu de l'écran approprié au contexte du constructeur */}
        <List
          loading={loading}
          profile={profile}
          constructorFunctions={constructorFunctions}
          constructorState={this.state}
          dataList={preventionPlans}
        />

        {/* Ouverture de la modale de création / modification */}
        <ManageModal
          width={800}
          loading={modalLoading}
          onCancel={this.safelyCloseModal}
          onOk={this.handleRowCreated}
          onChange={
            openedModal === 'updatePreventionPlan'
              ? this.updatePreventionPlan
              : this.createPreventionPlan
          }
          visible={includes(
            ['createPreventionPlan', 'updatePreventionPlan'],
            openedModal
          )}
          onValuesChange={this.onValuesChange}
          data={
            includes(['transition', 'updatePreventionPlan'], openedModal)
              ? {
                  id: get(preventionPlan, 'id'),
                  pdpnum: get(preventionPlan, 'pdpnum'),
                  name: get(preventionPlan, 'name'),
                  comment: get(preventionPlan, 'comment'),
                  typePdp: get(preventionPlan, 'typePdp'),
                  startDate: get(preventionPlan, 'startDate'),
                  endDate: get(preventionPlan, 'endDate'),
                  companies:
                    get(preventionPlan, 'companies')?.map(
                      company => company.id
                    ) || [],
                  subcontractors:
                    get(preventionPlan, 'subcontractors')?.map(
                      subcontractor => subcontractor.id
                    ) || [],
                  isAvenantOf: get(preventionPlan, 'isAvenantOf'),
                  isSigned: get(preventionPlan, 'isSigned')
                }
              : null
          }
          {...ModalSpecs}
        />
      </Fragment>
    )
  }
}

const mapStateToProps = state => {
  const defaultProps = get(PreventionPlansConstructor, 'defaultProps', {})

  console.log('state', state)
  // console.log('defaultProps', defaultProps)

  return {
    // PDP data - many
    preventionPlans: defaultTo(
      get(state, 'preventionPlan.items'),
      defaultProps.preventionPlans
    ),
    // PDP data - single
    preventionPlan: defaultTo(
      get(state, 'preventionPlan.item'),
      defaultProps.preventionPlan
    ),
    avenants: defaultTo(
      get(state, 'preventionPlan.avenants'),
      defaultProps.avenants
    ),
    // Relationnal data for fields
    companies: defaultTo(get(state, 'company.items'), defaultProps.companies),
    // API requests status
    isGathering:
      defaultTo(
        get(state, 'preventionPlan.isFetching'),
        defaultProps.isGathering
      ) ||
      defaultTo(
        get(state, 'preventionPlan.isFetchingItem'),
        defaultProps.isGathering
      ) ||
      defaultTo(get(state, 'preventionPlan.isFetchingAvenants'), false),
    isCreating: defaultTo(
      get(state, 'preventionPlan.isCreating'),
      defaultProps.isCreating
    ),
    isUpdating: defaultTo(
      get(state, 'preventionPlan.isUpdating'),
      defaultProps.isUpdating
    ),
    isFetchingCompanies: defaultTo(get(state, 'company.isFetching'), false),
    profile: defaultTo(get(state, 'profile.item'), defaultProps.profile)
  }
}

const mapDispatchToProps = dispatch =>
  console.log('preventionPlanActions', preventionPlanActions) || {
    actions: bindActionCreators(
      { ...preventionPlanActions, ...companyActions },
      dispatch
    )
  }

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PreventionPlansConstructor)
